import { memo, useState, useMemo } from "react";
import { BlockAdd, BlockRow } from "../../../../components/block/block";
import { Modal, Select } from "antd";

const filterSelectOption = (input, option) =>
  String(option?.label || "")
    .toLowerCase()
    .includes(input.toLowerCase());

export const InputList = memo(({ scenarioData = {}, inputType, boundTo = [], onChange, readonly = true }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null); // { id, type }
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setSelectedSource(null);
    setSelectedMessage(null);
    setEditIndex(null);
  };

  const buildOptions = () => {
    const groupOptions = (items, type, groupLabel) => ({
      label: groupLabel,
      options: items
        .map((item) => ({
          label: item.title,
          value: `${type}::${item.id}`,
          id: item.id,
          type,
          data: item.data,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "uk", { sensitivity: "base" })),
    });
    return [
      groupOptions(scenarioData.scenarios || [], "scenario", "Scenarios"),
      groupOptions(scenarioData.onboardings || [], "onboarding", "Onboarding"),
      groupOptions(scenarioData.dailySurveyTasks || [], "dailyTask", "Survey Tasks"),
      groupOptions(scenarioData.subjectInvitations || [], "enrollment", "Subject Invitation"),
      // LF360-1912. The dates the platform keeps about the subject itself.
      // Nothing is answered here — the "messages" are the dates — so the group
      // only has anything to offer a field whose input type is a date.
      groupOptions(scenarioData.subjectFields || [], "subject", "Subject profile"),
    ];
  };

  const allOptions = useMemo(buildOptions, [scenarioData]);
  const currentInputMessages = useMemo(() => {
    if (!selectedSource) return [];

    const source = allOptions
      .flatMap(group => group.options)
      .find((o) => o.id === selectedSource.id && o.type === selectedSource.type);
    const nodes = source?.data || {};

    return Object.values(nodes)
      .filter((node) => node?.data?.nodeType === inputType)
      .map((node) => {
        const isCurrent =
          editIndex !== null &&
          boundTo[editIndex]?.sourceId === selectedSource.id &&
          boundTo[editIndex]?.type === selectedSource.type &&
          boundTo[editIndex]?.nodeId === node.id;

        const alreadyBound = boundTo.some(
          (b, i) =>
            i !== editIndex &&
            b.sourceId === selectedSource.id &&
            b.type === selectedSource.type &&
            b.nodeId === node.id
        );

        return {
          label: node.data?.message || node.data?.title || "Unnamed",
          value: node.id,
          disabled: alreadyBound && !isCurrent
        };
      });
  }, [selectedSource, allOptions, inputType, boundTo, editIndex]);

  const handleScenarioChange = (value) => {
    const [type, id] = value.split("::");
    setSelectedSource({ id, type });
    setSelectedMessage(null);
  };

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 14 }}>
        <b>Input sources</b>
      </div>
      {boundTo.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <ul>
            {boundTo.map((b, idx) => {
              const sourceList = scenarioData[
                b.type === "scenario"
                  ? "scenarios"
                  : b.type === "onboarding"
                  ? "onboardings"
                  : b.type === "enrollment"
                  ? "subjectInvitations"
                  : b.type === "subject"
                  ? "subjectFields"
                  : "dailySurveyTasks"
              ] || [];

              const source = sourceList.find((s) => s.id === b.sourceId);
              const title = source?.title || "Unknown";

              return (
                <BlockRow
                  key={idx}
                  title={`${title}: ${b.message}`}
                  onClick={
                    readonly
                      ? undefined
                      : () => {
                          setEditIndex(idx);
                          setSelectedSource({ id: b.sourceId, type: b.type });
                          setSelectedMessage(b.nodeId);
                          setModalOpen(true);
                        }
                  }
                  onDelete={
                    readonly
                      ? undefined
                      : () => {
                          const updated = [...boundTo];
                          updated.splice(idx, 1);
                          onChange?.(updated);
                        }
                  }
                />
              );
            })}
          </ul>
        </div>
      )}
      {!readonly && (
        <BlockAdd
          title="Add input"
          onClick={openModal}
        />
      )}
      <Modal
        title={selectedSource ? "Edit input": "Add input"}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={() => {
          const source = allOptions
            .flatMap(group => group.options)
            .find(o => o.id === selectedSource.id && o.type === selectedSource.type);
          const node = Object.values(source?.data || {}).find((n) => n.id === selectedMessage);

          if (source && node) {
            const newBinding = {
              type: source.type,
              sourceId: source.id,
              nodeId: node.id,
              nodeType: node?.data?.nodeType,
              message: node?.data?.message || node?.data?.title || "Unnamed"
            };

            let updated;
            if (editIndex !== null) {
              updated = [...boundTo];
              updated[editIndex] = newBinding;
            } else {
              updated = [...boundTo, newBinding];
            }

            onChange?.(updated);
          }

          closeModal();
        }}
        okButtonProps={{
          disabled: !selectedSource || !selectedMessage,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>Source</div>
        <Select
          style={{ width: "100%" }}
          value={
            selectedSource
              ? `${selectedSource.type}::${selectedSource.id}`
              : null
          }
          onChange={handleScenarioChange}
          options={allOptions}
          showSearch
          optionFilterProp="label"
          filterOption={filterSelectOption}
          placeholder="Select a source"
          virtual={false}
          listHeight={320}
          getPopupContainer={() => document.body}
        />
        </div>

        <div>
          <div style={{ marginBottom: 4 }}>Input message</div>
          <Select
            style={{ width: "100%" }}
            value={selectedMessage}
            onChange={setSelectedMessage}
            options={currentInputMessages}
            showSearch
            optionFilterProp="label"
            filterOption={filterSelectOption}
            placeholder="Select a message"
            disabled={!selectedSource}
            virtual={false}
            listHeight={320}
            getPopupContainer={() => document.body}
          />
        </div>
      </Modal>
    </div>
  );
});
